import { POSProvider } from 'dtos/integrations/connection.dto';

// How each kind of store gets its data into Pellvr. Keep this honest: only the
// Pellvr Agent (Verifone/Gilbarco export folders) is automatic today; everything
// else is a guided file upload (CSV, Excel or XML) until a real connector exists.
export type IntakeMethod = 'agent' | 'upload';

export interface PosOption {
  id: string;
  name: string;
  examples: string;
  method: IntakeMethod;
  provider?: POSProvider; // connection provider for agent-fed POS
  badge?: string;
  priceBook: string[]; // steps to get the item list / price book
  sales: string[]; // steps to get item-level sales
  note?: string;
}

export const POS_OPTIONS: PosOption[] = [
  {
    id: 'verifone',
    name: 'Verifone',
    examples: 'Commander, Ruby2, C18, Topaz',
    method: 'agent',
    provider: 'verifone',
    badge: 'Automatic · beta',
    priceBook: ['The Pellvr Agent picks up the price book (PLU) and sales files your Commander already exports to the back-office PC. No agent yet? Report Navigator XML exports can be uploaded as they are.'],
    sales: ['Sales arrive automatically after each day close.'],
    note: 'Pellvr only reads export files. It never changes your POS or your current back-office program.',
  },
  {
    id: 'gilbarco-passport',
    name: 'Gilbarco Passport',
    examples: 'Passport POS',
    method: 'agent',
    provider: 'gilbarco-passport',
    badge: 'Automatic · beta',
    priceBook: ['The Pellvr Agent reads the store-close XML files Passport writes for back-office programs (item sales movement, price book) from the PPXMLData or BOOutBox share on the Passport server.'],
    sales: ['Sales arrive automatically after each day close.'],
    note: 'Pellvr only reads the files. If another back-office program already collects from BOOutBox, point Pellvr at PPXMLData so both keep working.',
  },
  {
    id: 'clover',
    name: 'Clover',
    examples: 'Clover Station, Mini, Flex',
    method: 'upload',
    badge: 'File upload',
    priceBook: ['In the Clover web dashboard, open Inventory → Items.', 'Choose Export and save the file.', 'Drop it on the Upload page as it is. Pellvr recognizes Clover exports.'],
    sales: ['Open Reports → Item sales, pick the last 30 days, and export.', 'Drop it on the Upload page. After that, once a week.'],
    note: 'A direct Clover connection is on our roadmap.',
  },
  {
    id: 'square',
    name: 'Square',
    examples: 'Square for Retail',
    method: 'upload',
    badge: 'File upload',
    priceBook: ['In Square Dashboard, open Items → Item library.', 'Choose Actions → Export library (Excel or CSV).', 'Drop it on the Upload page as it is. Pellvr recognizes Square exports.'],
    sales: ['Open Reports → Item sales, pick the last 30 days, and export the item details CSV.', 'Drop it on the Upload page. After that, once a week.'],
    note: 'A direct Square connection is on our roadmap.',
  },
  {
    id: 'nrs',
    name: 'NRS',
    examples: 'NRS POS, NRS Petro',
    method: 'upload',
    badge: 'File upload',
    priceBook: ['In the NRS web dashboard, export your item list (price book) to Excel or CSV.', 'Drop it on the Upload page as it is.'],
    sales: ['Export an item sales report for the last 30 days and drop it on the Upload page.', 'After that, once a week.'],
    note: 'Menu names differ between NRS versions. Send us a screenshot and we will point you to the right report.',
  },
  {
    id: 'back-office',
    name: 'Back-office software',
    examples: 'Modisoft, PDI / CStorePro, Petrosoft, Hisably',
    method: 'upload',
    badge: 'File upload',
    priceBook: ['From your back-office program, export the price book (item list with retail prices) as Excel, CSV or NAXML.', 'Drop it on the Upload page as it is.'],
    sales: ['Export item movement / item sales for the last 30 days and drop it in the same way.', 'After that, once a week.'],
    note: 'Using Verifone or Gilbarco too? The Pellvr Agent can read the POS files directly instead.',
  },
  {
    id: 'other',
    name: 'Something else',
    examples: 'NCR, Wayne Nucleus, other POS or spreadsheet',
    method: 'upload',
    badge: 'File upload',
    priceBook: ['Export your item list with barcode, name and shelf price (any CSV or Excel layout), or fill in our template.', 'Drop it on the Upload page.'],
    sales: ['Export item-level sales (item, date, quantity, amount) for the last 30 days and drop it in.'],
    note: 'Pellvr needs sales by item (barcode), not just daily totals. A cash register without scanning can still use invoices + your price list to find cost increases.',
  },
];

export const posOptionFor = (id: string | null | undefined) => POS_OPTIONS.find(option => option.id === id) ?? null;
